import React__default from 'react';
import { Text, Transforms, Editor, Path } from 'slate';
import { ListType, listTypes } from './constants.mjs';
import { isList, isListItem, getListTypeHotkey } from './utilities.mjs';
import { findNodes } from '../../../../core/queries/findNode.mjs';
import { HotkeyUtilities } from '../../../../../../utilities/hotkeys/index.mjs';
import { toggleList } from './transforms/toggleList.mjs';
import { List } from './components/List/List.mjs';
import { getRenderElement } from '../../../../core/rendering/getRenderElement.mjs';
import { getDerivedAttributes, getDerivedDataFromHTML } from '../../../../core/utilities/side-effects/combineSideEffects.mjs';
import { serialize } from '../../../../core/utilities/serialize/serialize.mjs';
import { getElementDeserializer } from '../../../../core/serialization/deserialize/utilities/getElementDeserializer.mjs';

const ListPlugin = options => ({
  type: ListType,
  onKeyDown: keyboardEvent => options !== null && options !== void 0 && options.onHotkey ? options.onHotkey(keyboardEvent) : undefined,
  renderElement: getRenderElement({
    type: ListType,
    rootProps: {},
    component(props) {
      const {
        listType
      } = props.element;
      return /*#__PURE__*/React__default.createElement(List, Object.assign({}, props, {
        type: listType
      }));
    }
  }),
  extendEditor(editor) {
    const {
      normalizeNode
    } = editor;
    editor.normalizeNode = entry => {
      const [node, path] = entry;
      if (isList(node)) {
        const {
          children
        } = node;

        // Remove empty lists
        if (children.length === 0 || children.length === 1 && Text.isText(children[0]) && !children[0].text.trim()) {
          Transforms.removeNodes(editor, {
            at: path
          });
          return;
        }

        // Unwrap lists that contain invalid list items
        if (!children.every(isListItem)) {
          Transforms.unwrapNodes(editor, {
            at: path
          });
          // ListItems will be unwraped automatically during the next normalization pass
          return;
        }

        // Combine adjacent lists of the same type
        for (const lookup of [Editor.previous, Editor.next]) {
          const sibling = lookup(editor, {
            at: path
          });
          if (sibling) {
            const [adjacentNode, adjacentNodePath] = sibling;
            if (isList(adjacentNode) && adjacentNode.listType === node.listType) {
              Transforms.mergeNodes(editor, {
                at: lookup === Editor.previous ? path : adjacentNodePath
              });
              return;
            }
          }
        }
      }

      // Lift up lists that were nested inside other elements
      if (Path.equals(path, [0])) {
        const listEntries = findNodes(editor, {
          match: isList
        });

        // [0] is a common ancestor node to all nodes. If the list is not a direct child of [0], it means it is nested deeper
        // and needs to be lifted up
        const nestedListEntries = listEntries.filter(([, listPath]) => !Path.isChild(listPath, path));
        if (nestedListEntries.length) {
          Transforms.liftNodes(editor, {
            at: path,
            match: matchNode => nestedListEntries.some(([nestedList]) => nestedList === matchNode)
          });
          return;
        }
      }
      normalizeNode(entry);
    };
    return editor;
  },
  serialize: {
    element(props) {
      const attributes = getDerivedAttributes({
        element: props.element
      });
      const {
        listType
      } = props.element;
      const tagName = listType === 'ordered' ? 'ol' : 'ul';
      return serialize(tagName, props.children, attributes);
    }
  },
  deserialize: {
    element: getElementDeserializer({
      type: ListType,
      rules: [{
        nodeNames: ['OL', 'UL']
      }],
      node: el => {
        const listNodeData = {
          type: ListType,
          listType: el.tagName === 'OL' ? 'ordered' : 'unordered'
        };
        return getDerivedDataFromHTML({
          data: listNodeData,
          htmlElement: el
        });
      }
    })
  }
});
const listHotKeyHandler = (editor, event, defaultBlockType) => {
  listTypes.forEach(listType => {
    const hotkey = getListTypeHotkey(listType);
    const shortcut = `Ctrl+Shift+${hotkey}`;
    const {
      altKey,
      ctrlKey,
      metaKey,
      shiftKey
    } = HotkeyUtilities.parseKeyboardShortcut(shortcut);
    const isHotKeyMatched = event.key === hotkey && event.altKey === altKey && event.ctrlKey === ctrlKey && event.metaKey === metaKey && event.shiftKey === shiftKey;
    if (isHotKeyMatched) {
      toggleList(editor, {
        at: editor.selection,
        listType,
        defaultBlockType
      });
    }
  });
};

export { ListPlugin, listHotKeyHandler };
